import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { MAX_PLAN_DEPTH } from '@timelog/pm-shared';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { usePlanEditor } from '../editor';

import { outlineSummary, outlineToNodes, parseOutline, shiftLines } from './outline';
import { OutlinePreview } from './OutlinePreview';

/** Appended at the end of the plan — capture adds phases, it does not reorganise. */
const ROOT_APPEND = { parentId: null, childIndex: Number.MAX_SAFE_INTEGER };

export type CaptureTasksDialogProps = {
  open: boolean;
  onClose: () => void;
};

/**
 * Bulk plan entry: write or paste an indented outline, watch the structure build
 * on the right, commit it as ONE document revision.
 *
 * The textarea is raw rather than a MUI `TextField multiline` because Tab has to
 * be intercepted and the selection restored by hand — `shiftLines` returns the
 * range to re-apply, since React re-rendering the value resets the caret.
 */
export const CaptureTasksDialog = ({ open, onClose }: CaptureTasksDialogProps) => {
  const { t } = useTranslation('pmPlanning');
  const { dispatch } = usePlanEditor();
  const [text, setText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const pendingSelection = useRef<{ start: number; end: number } | null>(null);

  const lines = useMemo(() => parseOutline(text), [text]);
  const summary = outlineSummary(lines);

  // Re-applied after React commits the shifted text, not before it.
  useLayoutEffect(() => {
    const selection = pendingSelection.current;
    const element = textareaRef.current;
    if (!selection || !element) return;
    pendingSelection.current = null;
    element.setSelectionRange(selection.start, selection.end);
  }, [text]);

  const commit = () => {
    const nodes = outlineToNodes(lines);
    if (nodes.length === 0) return;
    dispatch({ type: 'INSERT_NODES', nodes, position: ROOT_APPEND });
    // Everything captured should be visible; a fold left over from before the
    // capture would hide part of what was just added.
    dispatch({ type: 'UNFOLD_ALL' });
    onClose();
  };

  const onKeyDown = (event: ReactKeyboardEvent<HTMLTextAreaElement>) => {
    /*
      Deliberately does NOT stopPropagation: MUI's Dialog closes on Escape from a
      keydown listener above this textarea, so swallowing every key here made the
      dialog impossible to dismiss with the keyboard. The plan's own navigation is
      already inert while this is open — it ignores textareas, and PlanPanel
      passes `enabled: false`.
    */
    if (event.key === 'Tab') {
      event.preventDefault();
      const element = event.currentTarget;
      const result = shiftLines(
        element.value,
        element.selectionStart,
        element.selectionEnd,
        event.shiftKey ? 'out' : 'in',
      );
      pendingSelection.current = { start: result.selectionStart, end: result.selectionEnd };
      setText(result.text);
      return;
    }

    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      commit();
    }
    // Escape is left to MUI's Dialog, which already closes on escapeKeyDown.
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-automation-id="capture-dialog"
      // The dialog owns the full height so the textarea and the preview scroll
      // independently instead of the whole sheet growing.
      slotProps={{ paper: { sx: { maxHeight: '86vh' } } }}
      // Fresh sheet every time: a half-finished outline from a previous open
      // would silently re-commit.
      //
      // Focus lands in `onEntered`, not on the textarea's `autoFocus`: MUI's
      // Dialog wraps its children in a FocusTrap whose mount effect pulls focus
      // to the paper if it is not already inside, which lands after `autoFocus`
      // and beats it. Focusing once the transition has finished is the only
      // point where nothing else is competing for it.
      TransitionProps={{
        onEnter: () => setText(''),
        onEntered: () => textareaRef.current?.focus(),
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        {t('planPanel.CaptureTasks')}
        <Typography variant="label" component="p" sx={{ color: 'var(--fg-3)', mt: 0.5 }}>
          {t('capture.Intro')}
        </Typography>
      </DialogTitle>

      <DialogContent
        sx={{ p: 0, display: 'flex', minHeight: 0, borderTop: '1px solid var(--border-divider)' }}
      >
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <Box
            component="textarea"
            ref={textareaRef}
            data-automation-id="capture-input"
            value={text}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={onKeyDown}
            // A sample outline, in the UI language: it is a placeholder, never seeded text.
            placeholder={t('capture.Placeholder')}
            spellCheck={false}
            autoFocus
            sx={{
              flex: 1,
              minHeight: 320,
              resize: 'none',
              border: 'none',
              outline: 'none',
              px: 2.5,
              py: 2,
              fontFamily: 'var(--font-family-mono)',
              fontSize: 14,
              lineHeight: 1.85,
              color: 'var(--fg-1)',
              bgcolor: 'transparent',
              tabSize: 2,
              // The sample outline is a placeholder, never seeded text: the
              // field opens focused AND empty, so the ghost survives until the
              // first keystroke and nothing is committed by accident. Firefox
              // dims placeholders by default — pin the opacity so "ghosted"
              // means one colour everywhere.
              '&::placeholder': { color: 'var(--fg-3)', opacity: 1 },
            }}
          />
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              gap: 2,
              px: 2.5,
              py: 1,
              borderTop: '1px solid var(--border-divider)',
              bgcolor: 'var(--bg-sunken)',
            }}
          >
            <KeyHint keys="↵" label={t('capture.NextTask')} />
            <KeyHint keys="Tab" label={t('capture.Indent')} />
            <KeyHint keys="⇧ Tab" label={t('capture.Outdent')} />
            <KeyHint keys="⌘ ↵" label={t('capture.AddToPlanHint')} />
          </Stack>
        </Stack>

        <OutlinePreview lines={lines} />
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: 'space-between',
          gap: 2,
          px: 2.75,
          py: 1.5,
          borderTop: '1px solid var(--border-divider)',
          bgcolor: 'var(--bg-sunken)',
        }}
      >
        <Typography
          variant="label"
          sx={{ color: 'var(--fg-3)' }}
          data-automation-id="capture-summary"
        >
          {summary.total === 0
            ? t('capture.NothingCaptured')
            : t('planPanel.TaskCount', { count: summary.total })}
          {summary.clampedCount > 0 ? ` · ${t('capture.Flattened', { max: MAX_PLAN_DEPTH })}` : ''}
        </Typography>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
          <Button onClick={onClose} color="inherit" data-automation-id="capture-cancel">
            {t('capture.Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={commit}
            disabled={summary.total === 0}
            data-automation-id="capture-commit"
          >
            {summary.total === 0
              ? t('capture.AddToPlan')
              : t('capture.AddCountToPlan', { count: summary.total })}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

const KeyHint = ({ keys, label }: { keys: string; label: string }) => (
  <Stack direction="row" sx={{ alignItems: 'center', gap: 0.625 }}>
    <Box
      component="kbd"
      sx={{
        fontFamily: 'var(--font-family-mono)',
        fontSize: 10,
        bgcolor: 'var(--bg-panel)',
        border: '1px solid var(--border-divider)',
        borderRadius: 'var(--border-radius-small)',
        px: 0.75,
        color: 'var(--fg-1)',
      }}
    >
      {keys}
    </Box>
    <Typography variant="label" sx={{ color: 'var(--fg-3)' }}>
      {label}
    </Typography>
  </Stack>
);
